import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import { z } from "zod";

export type FeeScheduleJSON = z.infer<typeof FeeScheduleSchema.FeeScheduleJSONZ>;
export type FeeScheduleParams = z.infer<typeof FeeScheduleSchema.FeeScheduleZ>;

export default class FeeScheduleSchema {
  public static readonly FeeScheduleJSONZ = z.object({
    workAgreementId: z.uuid(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ).nullish(),
    clientFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
    vendorFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
    hireDate: z.iso.datetime({ offset: true }).nullish(),
    archiveDate: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly FeeScheduleZ = z.object({
    workAgreementId: z.instanceof(Guid),
    categories: z.array(z.union([z.instanceof(FeeScheduleCategory), z.instanceof(FeeScheduleCategoryMaskedCurrency)])).optional(),
    clientFeeScheduleTemplateIds: z.array(z.instanceof(Guid)).optional(),
    vendorFeeScheduleTemplateIds: z.array(z.instanceof(Guid)).optional(),
    hireDate: z.custom<AHDate>((value) => value instanceof AHDate && value.isValid).optional(),
    archiveDate: z.custom<AHDate>((value) => value instanceof AHDate && value.isValid).optional(),
  });

  public static validate(values: unknown): FeeScheduleParams {
    return parseOrThrow(FeeScheduleSchema.FeeScheduleZ, values, {
      logError: true,
      schemaName: "FeeScheduleSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): FeeScheduleJSON {
    return parseOrThrow(FeeScheduleSchema.FeeScheduleJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleParams {
    try {
      FeeScheduleSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FeeScheduleJSON {
    try {
      FeeScheduleSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleJSON): FeeScheduleParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(FeeScheduleSchema.FeeScheduleJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return FeeScheduleSchema.codec.decode(parsed);
  }

  public static serialize(feeSchedule: FeeScheduleParams): FeeScheduleJSON {
    if (isNullOrUndefined(feeSchedule)) return feeSchedule;
    const parsed = FeeScheduleSchema.validate(feeSchedule);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeScheduleSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(FeeScheduleSchema.FeeScheduleJSONZ, FeeScheduleSchema.FeeScheduleZ, {
    decode: (data) => ({
      workAgreementId: new Guid(data.workAgreementId),
      categories: data.categories ? data.categories.map((category) => FeeScheduleCategory.fromJSON(category)) : [],
      clientFeeScheduleTemplateIds: data.clientFeeScheduleTemplateIds
        ? data.clientFeeScheduleTemplateIds.map((id) => new Guid(id))
        : [],
      vendorFeeScheduleTemplateIds: data.vendorFeeScheduleTemplateIds
        ? data.vendorFeeScheduleTemplateIds.map((id) => new Guid(id))
        : [],
      hireDate: AHDate.fromJSON(data.hireDate) ?? undefined,
      archiveDate: AHDate.fromJSON(data.archiveDate) ?? undefined,
    }),
    encode: (feeSchedule) => ({
      workAgreementId: feeSchedule.workAgreementId.value,
      categories: feeSchedule.categories
        ? feeSchedule.categories.map((category) => {
            const serializedCategory = FeeScheduleCategorySchema.serialize(category);
            if (category instanceof FeeScheduleCategoryMaskedCurrency) {
              return { ...serializedCategory, currencyIsMasked: true };
            }
            return serializedCategory;
          })
        : [],
      clientFeeScheduleTemplateIds: feeSchedule.clientFeeScheduleTemplateIds
        ? feeSchedule.clientFeeScheduleTemplateIds.map((id) => id.value)
        : [],
      vendorFeeScheduleTemplateIds: feeSchedule.vendorFeeScheduleTemplateIds
        ? feeSchedule.vendorFeeScheduleTemplateIds.map((id) => id.value)
        : [],
      hireDate: feeSchedule.hireDate?.toISOString(),
      archiveDate: feeSchedule.archiveDate?.toISOString(),
    }),
  });
}
